/**
 * External-image helper — decides whether to load a channel-art URL
 * directly from its source host or route it through the backend proxy.
 *
 * Context: our production ingress rewrites `Cache-Control` on every
 * `/api/*` response to `no-store, no-cache, must-revalidate`, which
 * kills the browser's HTTP cache. The backend proxy sets a 7-day
 * immutable header at origin, but by the time it reaches the browser
 * the header is gone. Net result: every channel poster re-downloads
 * on every guide open.
 *
 * Workaround: for images already hosted on a CDN with sensible cache
 * headers (TMDb, postimg, imgur), hit the source host DIRECTLY on the
 * frontend — bypassing our proxy entirely. Those CDNs already carry
 * long `Cache-Control: public, max-age=...` headers, so the browser
 * happily caches them and re-visits are instant.
 *
 * For local uploads (`*_path` fields, served by our proxy from object
 * storage) there's no external URL to hit — the proxy is the only
 * source, and we accept the platform's no-store override for those.
 */

// Hosts that we KNOW ship long `Cache-Control` headers on their image
// responses. Extend this list as more owner-curated hosts show up in
// the wild — every entry saves us a proxy hop and (more importantly)
// dodges the ingress `no-store` rewrite so the browser can actually
// cache the bytes.
//
// Match rule: substring on the URL's hostname (lowercased) after any
// protocol strip. Substring lets us hit both `image.tmdb.org` and any
// future TMDb subdomain, and covers postimg's `i.postimg.cc` +
// `postimg.cc` variants with a single entry.
const CACHED_HOSTS = [
  "image.tmdb.org",   // TMDb — Cloudfront, 1yr cache
  "postimg.cc",       // postimg.cc + i.postimg.cc
  "imgur.com",        // imgur direct-image URLs (i.imgur.com)
  "githubusercontent.com", // raw github artwork, aggressive cache
  "cloudfront.net",   // catch-all for direct CloudFront-hosted art
];

/**
 * Return true when the URL points at a host we trust to serve its
 * own cache headers. Safe against malformed input.
 */
export function isBrowserCacheableHost(url) {
  if (!url || typeof url !== "string") return false;
  let host;
  try { host = new URL(url).hostname.toLowerCase(); }
  catch { return false; }
  return CACHED_HOSTS.some((h) => host.includes(h));
}
